import { Comment } from './comment.interface';
import { CommentaryItemInterface } from './commentary.interface';
import {
  ActionBox,
  ImplementationTimelineEnum,
  ImplementationTimelineFieldNames,
  SeverityEnum,
  SeverityFieldNames,
} from './general.interface';
import { RiskStatus } from './risk-simulation.interface';
import { PolicyStatus } from './policies.interface';

export enum TasksSortingByEnum {
  DATE_TIME_CREATED = 'DateTimeCreated',
  DATE_TIME_SLA = 'dateTimeSLA',
  DATE_TIME_TASK_STATUS = 'dateTimeTaskStatus',
  OWNER_FULL_NAME = 'ownerFullName',
  TASK_PRIORITY = 'myCMTaskPriority',
  TASK_STATUS = 'myCMTaskStatus',
  TASK_FULL_DESC = 'cmTaskFullDescription',
}

export enum TaskStatusEnumOld {
  New = 0,
  Assigned = 1,
  InProgress = 2,
  Freeze = 3,
  Pending = 4,
  Completed = 5,
  Canceled = 6,
}

export enum TaskTypeEnum {
  NeedUser = 0,
  FromProject = 1,
  SecurityControl = 2,
  CustomerRequest = 3,
  FromProgram = 4,
  Internal = 5,
}

export enum TaskPriorityEnum {
  Low = 0,
  Medium = 1,
  High = 2,
  Urgent = 3,
}

export enum TaskStatusEnum {
  pending = 1,
  inProgress,
  completed,
  rejected,
  blocked,
  acceptedRisk,
}

export interface UpdateTaskStatusRequest {
  taskId: string;
  status: number;
  outputText?: string | null;
}

export interface StatusDto {
  id: TaskStatusEnum;
  name: string;
  description: string;
  selectable: boolean;
}

export enum ReferenceTypeEnum {
  POLICY = 'policy',
  RISK = 'risk',
  TASK = 'task',
  CONTROL = 'control',
}

export interface TaskChangeStatusEvent {
  taskId: string;
  myCMTaskStatus: TaskStatusEnumOld;
  prevStatus: TaskStatusEnumOld;
}

export interface TasksParamsRequestInterface {
  pageSize: number;
  pageNumber: number;
  sortBy?: TasksSortingByEnum;
  sortOrderDesc?: boolean;
}

export interface Task {
  asset: {
    name: string;
  };
  dueBy: string;
  id: string;
  isRecurring?: boolean;
  isReviewed: boolean;
  name: string;
  ownerId: string;
  ownerName: string;
  ownerDepartmentId: string;
  priority: number;
  activeActionId: string;
  taskLocked: boolean;
  isBlocked: boolean;
  isBlockedByPrerequisites: boolean;
  taskLockedReasonId?: string;
  taskStatusId: TaskStatusEnum;
  severity: SeverityEnum;
  requiresOutput: boolean;
  /** Canonical output prompts (Task 2531 T180 / M4A). */
  outputPromptEn?: string | null;
  outputPromptHe?: string | null;
  outputPromptAr?: string | null;
  raci: TaskAssignments;
  domains: TaskDomain[];
  implementationTimeline: ImplementationTimelineEnum;
  controlIds?: string[];
  allDepartments: { id: string; name: string }[];
  canChangeStatus: boolean;
}

export interface TaskDomain {
  domainId: string;
  domainName: string;
}

export interface TaskAssignments {
  departments: TaskAssignedRoles;
  users: TaskAssignedRoles;
}
export enum StakeholderRoles {
  OWNER = 'owner',
  ACCOUNTABLE = 'accountables',
  CONSULTEDS = 'consulteds',
  INFORMDES = 'informeds',
}

export interface TaskAssignedRoles {
  [StakeholderRoles.OWNER]?: {
    id: string;
    name: string;
    isCurrentUser?: boolean;
  }[];
  [StakeholderRoles.ACCOUNTABLE]?: {
    id: string;
    name: string;
    isCurrentUser?: boolean;
  }[];
  [StakeholderRoles.CONSULTEDS]?: {
    id: string;
    name: string;
    isCurrentUser?: boolean;
  }[];
  [StakeholderRoles.INFORMDES]?: {
    id: string;
    name: string;
    isCurrentUser?: boolean;
  }[];
}

export interface TaskDetailsStakeholders {
  [StakeholderRoles.OWNER]?: {
    userId: string;
    userName: string;
    depName: string;
    depCount: number;
    depId: string;
    isCurrentUser?: boolean;
  };
  [StakeholderRoles.ACCOUNTABLE]?: {
    userId: string;
    userName: string;
    depName: string;
    depCount: number;
    depId: string;
    isCurrentUser?: boolean;
  }[];
  [StakeholderRoles.CONSULTEDS]?: {
    userId: string;
    userName: string;
    depName: string;
    depCount: number;
    depId: string;
    isCurrentUser?: boolean;
  }[];
  [StakeholderRoles.INFORMDES]?: {
    userId: string;
    userName: string;
    depName: string;
    depCount: number;
    depId: string;
    isCurrentUser?: boolean;
  }[];
}

export interface TaskDependency {
  taskId: string;
  taskName: string;
  assetName: string;
}

export interface TaskDetails {
  taskId: string;
  actionBox: ActionBox;
  taskName: string;
  taskDescription: string;
  tasksStakeholders: TaskDetailsStakeholders;
  stakeholders: any[];
  taskStatus: TaskStatusEnum;
  taskReferences: {
    referenceType: ReferenceTypeEnum;
    name: string;
    status: PolicyStatus | RiskStatus;
    statusIcon?: string;
  }[];
  remediationSteps: string;
  comments?: Comment[];
  totalSimilarTasks: number;
  totalSimilarTasksInstatusClose: number;
  taskDetailsCommentPending?: boolean;
  taskDetailsCommentFailed?: boolean;
  newComment?: Comment;
  commentErrorMessage?: string;
  isBlockedByPrerequisites: boolean;
  blockingPrerequisiteTasks: TaskDependency[];
}

export interface TaskCoverage {
  id: string;
  totalActiveTasksCount: number;
  openTasksBySeverity: TasksBySeverity;
  openTasksByTimeline: TasksByTimeline;
  severityCompletion: TaskSeverityCompletion;
  openTasksByStakeholders: TasksByStakeholders[];
  openTasksBySecurityDomain: TasksBySecurityDomain[];
  allTasksBySecurityDomain: TasksBySecurityDomain[];
}

export interface TasksBySeverity {
  [SeverityFieldNames.CRITICAL]: number;
  [SeverityFieldNames.HIGH]: number;
  [SeverityFieldNames.MEDIUM]: number;
  [SeverityFieldNames.LOW]: number;
}

export interface TasksByTimeline {
  [ImplementationTimelineFieldNames.OVERDUE]: number;
  [ImplementationTimelineFieldNames.IMMEDIATE]: number;
  [ImplementationTimelineFieldNames.TWO_WEEKS]: number;
  [ImplementationTimelineFieldNames.NO_RUSH]: number;
}

export interface TasksBySecurityDomain {
  name: string;
  percentage: number;
  domainSize: number;
  totalSeverity?: SeverityEnum;
  severityForDomain: SeverityForDomains;
  color: string;
}

export interface SeverityForDomains {
  [SeverityFieldNames.CRITICAL]: number;
  [SeverityFieldNames.HIGH]: number;
  [SeverityFieldNames.MEDIUM]: number;
  [SeverityFieldNames.LOW]: number;
}

export interface TasksByStakeholders {
  name: string;
  percentage: number;
}

export interface TaskSeverityCompletion {
  [SeverityFieldNames.CRITICAL]: TaskSeverityCompletionItem;
  [SeverityFieldNames.HIGH]: TaskSeverityCompletionItem;
  [SeverityFieldNames.MEDIUM]: TaskSeverityCompletionItem;
  [SeverityFieldNames.LOW]: TaskSeverityCompletionItem;
}

export interface TaskSeverityCompletionItem {
  totalCount: number;
  doneCount: number;
}

export interface TasksResponseInterface<T> {
  data: T;
  hasErrors: boolean;
  isSystemError: boolean;
  errors: any;
}

export interface GetTasksResponseInterface {
  pageSize: number;
  pageNumber: number;
  numberOfRecords: number;
  list: TaskItemInterface[];
  data: any;
}

export interface TaskAdditionalParamsInterface {
  value: number;
  label: string;
}

export interface TaskItemInterface {
  id: string;
  tenantID: string;
  ownerID: string | null;
  ownerFullName: string | null;
  dateTimeCreated: string;
  dateTimeTaskStatus: string;
  dateTimeSLA: string;
  myCMTaskStatus: TaskStatusEnumOld;
  myCMTaskType: number;
  myCMTaskPriority: number;
  cmTaskSubject: string;
  cmTaskFullDescription: string;
  cmTaskComment: string;
  recurrenceSetting: string;
  createdByUserID: string;
  referencesIds?: string;
  cmTags: string;
  isTaskRecurring: boolean;
}

export interface TaskFormInterface {
  id?: string;
  ownerID?: string | null;
  dateTimeSLA?: string | null;
  dateTimeCreated: string;
  myCMTaskStatus?: TaskStatusEnumOld;
  myCMTaskPriority: TaskPriorityEnum;
  cmTaskSubject: string;
  cmTaskFullDescription: string;
  cmTags: string | string[];
  transforFromObjID?: string;
  myCMMessageTransformFrom?: string;
  recurrenceSetting?: RecurrenceSettingInterface | null | string | {};
  scheduleType?: string;
  cmTaskComments: CommentaryItemInterface[];
  CMTaskTransformFrom?: string;
}

export interface RecurrenceSettingInterface {
  StartDateTime: string | null;
  EndDateTime: string | null;
  StartTime?: string | null;
  EndTime?: string | null;
  RecurrenceType: string;
  Interval: string;
  DaysOfWeek: string[];
  TimeZoneName: string;
  DaysOfYearIndex?: null | string;
  DaysOfMonthIndex?: null | string;
  StartTimeFormat?: null | string;
  EndTimeFormat?: null | string;
}

export interface CommentRequest {
  taskId: string;
  comment: string;
}

export interface TaskMenuActionEvent {
  action: string;
  taskId: string;
  taskItem: TaskItemInterface;
}

export const STAKEHOLDER_CHART_COLORS = [
  '#4B3DE7',
  '#5D9891',
  '#88DBF5',
  '#2088FE',
  '#8542F3',
  '#01C0B0',
];

export const CHART_COLORS = [
  '#4B3DE7',
  '#5D9891',
  '#88DBF5',
  '#2088FE',
  '#8542F3',
  '#01C0B0',
  '#E8983D',
  '#83CC6E',
  '#024FE6',
  '#BE17DF',
  '#FF585B',
];

export const TASK_STATUS_COLORS = {
  [TaskStatusEnum.pending]: '#8542F3',
  [TaskStatusEnum.inProgress]: '#01C0B0',
  [TaskStatusEnum.completed]: '#2088FE',
  [TaskStatusEnum.rejected]: '#E8983D',
  [TaskStatusEnum.blocked]: '#FF585B',
  [TaskStatusEnum.acceptedRisk]: '#83CC6E',
};

export const SEVERITY_STATUS_COLORS = {
  [SeverityFieldNames.CRITICAL]: 'var(--color-severity-low)',
  [SeverityFieldNames.HIGH]: 'var(--color-severity-medium)',
  [SeverityFieldNames.MEDIUM]: 'var(--color-severity-good)',
  [SeverityFieldNames.LOW]: 'var(--color-severity-excellent)',
};
